import { formatarHora } from './datas';
import { totalDoPedido } from './dinheiro';
import type { Pedido } from './dominio';
import { primeiroNome } from './mensagens';
import { type RelogioDoPedido, relogioDoPedido } from './relogio';
import { plural } from './texto';

// A lista do balcão em grupos fixos (decisão B5): Resolver (não entregue), Para sair, Na rua (por
// entregador) e Encerrados. Genérica no pedido: a tela passa PedidoNaLista e recebe o mesmo tipo de volta.

export type PedidoDaFila = Pick<
  Pedido,
  | 'id'
  | 'numero'
  | 'status'
  | 'criadoEm'
  | 'saiuEm'
  | 'concluidoEm'
  | 'entregadorId'
  | 'valorCentavos'
  | 'taxaEntregaCentavos'
  | 'pagamentos'
  | 'trocoParaCentavos'
  | 'motivo'
> & { clienteNome: string; entregadorNome: string | null };

export interface LinhaDaFila<P extends PedidoDaFila> {
  pedido: P;
  relogio: RelogioDoPedido;
}

export interface GrupoNaRua<P extends PedidoDaFila> {
  entregadorId: string | null;
  entregadorNome: string;
  linhas: LinhaDaFila<P>[];
}

export interface ResumoDoDia {
  entregues: number;
  faturadoCentavos: number;
}

export interface FilaDoBalcao<P extends PedidoDaFila> {
  /** Não entregues: decidir se sai de novo ou cancela. O que voltou primeiro fica em cima. */
  resolver: P[];
  /** Na loja, o mais antigo em cima. */
  paraSair: LinhaDaFila<P>[];
  /** A caminho, por entregador; grupos e linhas pelo pedido mais antigo. */
  naRua: GrupoNaRua<P>[];
  /** Criados na janela. */
  encerrados: { entregues: P[]; cancelados: P[] };
  /** Na loja ou na rua com o prazo estourado, o mais atrasado primeiro. */
  atrasados: LinhaDaFila<P>[];
  /** Mesma regra de analisarPedidos: criados na janela, entregues, produtos + taxa. */
  resumo: ResumoDoDia;
}

const SEM_ENTREGADOR = 'Sem entregador';

const instante = (iso: string | null) => (iso ? Date.parse(iso) : Number.POSITIVE_INFINITY);
const pelaCriacao = (a: PedidoDaFila, b: PedidoDaFila) =>
  instante(a.criadoEm) - instante(b.criadoEm) || a.numero - b.numero;

export function filaDoBalcao<P extends PedidoDaFila>(
  pedidos: readonly P[],
  agora: Date,
  prazoMin: number,
  janela: { inicio: string; fim: string },
): FilaDoBalcao<P> {
  const inicio = Date.parse(janela.inicio);
  const fim = Date.parse(janela.fim);
  const naJanela = (p: P) => {
    const t = Date.parse(p.criadoEm);
    return t >= inicio && t < fim;
  };
  const linha = (pedido: P): LinhaDaFila<P> => ({
    pedido,
    relogio: relogioDoPedido(pedido, agora, prazoMin),
  });
  const ordenados = [...pedidos].sort(pelaCriacao);

  const resolver = ordenados
    .filter((p) => p.status === 'nao_entregue')
    .sort((a, b) => instante(a.concluidoEm) - instante(b.concluidoEm) || pelaCriacao(a, b));
  const paraSair = ordenados.filter((p) => p.status === 'pendente').map(linha);

  const grupos = new Map<string, GrupoNaRua<P>>();
  for (const p of ordenados) {
    if (p.status !== 'a_caminho') continue;
    const chave = p.entregadorId ?? '';
    const grupo = grupos.get(chave) ?? {
      entregadorId: p.entregadorId,
      entregadorNome: p.entregadorNome ?? SEM_ENTREGADOR,
      linhas: [],
    };
    grupo.linhas.push(linha(p));
    grupos.set(chave, grupo);
  }
  // Os grupos nascem na ordem do pedido mais antigo de cada um (ordenados já vem pela criação).
  const naRua = [...grupos.values()];

  const atrasados = [...paraSair, ...naRua.flatMap((g) => g.linhas)]
    .filter((l) => l.relogio.estado === 'atrasou')
    .sort((a, b) => b.relogio.minutos - a.relogio.minutos || pelaCriacao(a.pedido, b.pedido));

  const doDia = ordenados.filter(naJanela);
  const entregues = doDia.filter((p) => p.status === 'entregue');
  const cancelados = doDia.filter((p) => p.status === 'cancelado');

  return {
    resolver,
    paraSair,
    naRua,
    encerrados: { entregues, cancelados },
    atrasados,
    resumo: {
      entregues: entregues.length,
      faturadoCentavos: entregues.reduce((soma, p) => soma + totalDoPedido(p), 0),
    },
  };
}

/** Até quantos cancelados aparecem com nome e hora. */
const CANCELADOS_COM_NOME = 2;

/** "17 entregues · 1 cancelado (Gustavo, 12:10)". Os nomes só até 2 cancelados. */
export function textoDosEncerrados(
  e: FilaDoBalcao<PedidoDaFila>['encerrados'],
  fuso: string,
): string {
  const partes: string[] = [];
  if (e.entregues.length) partes.push(plural(e.entregues.length, 'entregue', 'entregues'));
  if (e.cancelados.length) {
    const quantos = plural(e.cancelados.length, 'cancelado', 'cancelados');
    const nomes =
      e.cancelados.length <= CANCELADOS_COM_NOME
        ? ` (${e.cancelados
            .map((p) => `${primeiroNome(p.clienteNome)}, ${formatarHora(p.criadoEm, fuso)}`)
            .join('; ')})`
        : '';
    partes.push(`${quantos}${nomes}`);
  }
  return partes.length ? partes.join(' · ') : 'nenhum encerrado ainda';
}
